import { useMutation } from '@apollo/client';
import { Cancel as CancelIcon, Check as CheckIcon } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  TextField,
  Typography,
} from '@mui/material';
import { amber, green } from '@mui/material/colors';
import * as Schema from 'generated/graphql/schema';
import { FunctionComponent, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import TextContentArea from '@/components/containers/text-content-area';
import AndonCallDialogHeader from '@/components/dialogs/andon/header';
import { Context } from '@/contexts/company';
import { andonCallCancel, andonCallTake } from '@/graphql/mutations';
import { andonListCalls } from '@/graphql/queries';
import { buildSummary } from '@/helpers/andon';
import { guardedCallback } from '@/helpers/helper-functions';
import { useUtils } from '@/hocs';

const useStyles = makeStyles()((theme) => ({
  iconSizeLeft: {
    color: 'inherit',
    height: '1em !important',
    width: '1em !important',
    fontSize: theme.typography.pxToRem(24),
    marginRight: theme.spacing(),
  },
  iconSizeRight: {
    color: 'inherit',
    height: '1em !important',
    width: '1em !important',
    fontSize: theme.typography.pxToRem(24),
    marginLeft: theme.spacing(),
  },
  buttonWithIcon: {
    '& > span > svg': {
      marginLeft: theme.spacing(),
    },
  },
  buttonWithIconLeft: {
    '& > span > svg': {
      marginRight: theme.spacing(),
    },
  },
  hideWhileSmall: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  buttonNeutral: {
    backgroundColor: amber[600],
    color: theme.palette.getContrastText(amber[600]),
    '&:hover': {
      backgroundColor: amber[600],
    },
  },
  buttonNegative: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.getContrastText(theme.palette.error.main),
    '&:hover': {
      backgroundColor: theme.palette.error.main,
    },
  },
  buttonPositive: {
    backgroundColor: green[600],
    color: theme.palette.getContrastText(green[600]),
    '&:hover': {
      backgroundColor: green[600],
    },
  },
}));

export interface Properties {
  call: Schema.AndonListCallsQuery['andon'][0]['calls'][0];
  requireInitials: boolean;
  onClose(..._: unknown[]): unknown;
}

const AndonTakeCallDialog: FunctionComponent<Properties> = ({ call, requireInitials, onClose }) => {
  const [t] = useTranslation();
  const { classes, cx } = useStyles();

  const baseState = {
    initials: '',
    submitAttempt: false,
  };

  const [initials, setInitials] = useState(baseState.initials);
  const [submitAttempt, setSubmitAttempt] = useState(baseState.submitAttempt);

  const onInitialsChange = useCallback(
    guardedCallback((event) => {
      setSubmitAttempt(baseState.submitAttempt);
      return setInitials(event);
    }),
    [],
  );

  const onExited = useCallback(() => {
    const states: [Function, string][] = [[setInitials, baseState.initials]];

    states.forEach(([setter, state]) => setter(() => state));
  }, []);

  const [take, { loading: taking }] = useMutation<Schema.AndonCallTakeMutation, Schema.AndonCallTakeMutationVariables>(
    andonCallTake,
  );
  const [cancel, { loading: cancelling }] = useMutation<
    Schema.AndonCallCancelMutation,
    Schema.AndonCallCancelMutationVariables
  >(andonCallCancel, { refetchQueries: [{ query: andonListCalls }] });

  const { companyId } = useContext(Context);

  const utils = useUtils();

  const callAction = call.action.make;
  if (!callAction || !companyId) {
    return null;
  }

  const textFields = [
    {
      id: 'initials',
      label: t(['shared:initials'], { defaultValue: 'Initials' }),
      rows: 1,
      value: initials,
      onChange: onInitialsChange,
      required: requireInitials,
      error: !initials.length,
    },
  ];

  const disableButton = textFields.some((field) => field.required && field.error);

  const summary = buildSummary(call.action, utils);

  return (
    <AndonCallDialogHeader onClose={onClose} onExited={onExited}>
      <DialogContent>
        <TextContentArea>
          <Typography variant="subtitle2">
            {t(['andon:call:requestedSupport'], { defaultValue: 'Requested support' })}
          </Typography>
          <Typography>{call.requestedSupport || t(['shared:any'], { defaultValue: 'Any' })}</Typography>
        </TextContentArea>
        <TextContentArea>
          <Typography variant="subtitle2">{t(['shared:summary'], { defaultValue: 'Summary' })}</Typography>
          <Typography>{summary}</Typography>
        </TextContentArea>
        {textFields.map(({ id, label, rows, value, onChange, required, error }, i) => {
          return (
            <TextContentArea key={id}>
              <FormControl variant="filled" fullWidth>
                <TextField
                  id={id}
                  label={label}
                  rows={rows}
                  autoFocus={i === 0}
                  variant="outlined"
                  onChange={onChange}
                  value={value}
                  required={required}
                  error={submitAttempt && error}
                />
              </FormControl>
            </TextContentArea>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" className={classes.hideWhileSmall}>
          {t(['shared:close'], { defaultValue: 'Close' })}
        </Button>
        <Button
          onClick={async () => {
            await cancel({
              variables: {
                id: call.id,
                companyId,
              },
            });

            onClose();
          }}
          variant="contained"
          className={cx(classes.buttonWithIconLeft, classes.buttonNegative)}
        >
          {cancelling ? <CircularProgress className={classes.iconSizeLeft} /> : <CancelIcon />}
          {t(['shared:cancel'], { defaultValue: 'Cancel' })}
        </Button>
        <Button
          onClick={async () => {
            if (requireInitials && !initials) {
              return;
            }

            await take({
              variables: {
                id: call.id,
                companyId,
                author: initials,
              },
            });

            onClose();
          }}
          color="primary"
          variant="contained"
          disabled={disableButton}
          className={cx(classes.buttonWithIconLeft, classes.buttonPositive)}
        >
          {taking ? <CircularProgress className={classes.iconSizeLeft} /> : <CheckIcon />}
          {t(['shared:take'], { defaultValue: 'Take' })}
        </Button>
      </DialogActions>
    </AndonCallDialogHeader>
  );
};

export default AndonTakeCallDialog;
